import { RefObject, SyntheticEvent, useContext, useEffect, useRef } from 'react';
import { Box, Chip, Grid } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { UserProfile } from '@/redux/slices/slice_types';
import { applyFilter, setSorting } from '@/redux/slices/dataTableSlice';
import { selectDataTableIsMultiSort, selectUserColumns, selectUserFiltedByType } from '@/redux/selectors/dataTableSelectors';
import PolarisIcon from '@/components/_common/PolarisIcon';
import UserDisplay from '@/components/_common/AssigneeSelect/UserDisplay';
import UserSearch from '@/components/_common/AssigneeSelect/UserSearch';
import FilterAccordion from '@/components/_common/Filters/FilterAccordion';

interface FilterUserProps {
  type?: 'assigned_users_filter' | 'managed_by_filter';
  isQuickFilters?: boolean;
  expandedFilters?: string[];
  onExpand?: (filter: string) => void;
  isAdvanceFilter?: boolean;
  advanceFilters?: Record<string, any>;
  onAdvanceFiltersChange?: (newAdvanceFilters: { type: string; selections: any }) => void;
}

const FilterUser = ({
  type = 'assigned_users_filter',
  isQuickFilters = false,
  expandedFilters = [],
  onExpand,
  isAdvanceFilter = false,
  advanceFilters = {},
  onAdvanceFiltersChange
}: FilterUserProps) => {
  const isFiltersV2 = useContext(ConfigContext)?.filtersVersion === 2;
  const dispatch = useAppDispatch();
  const selectedUsers = isAdvanceFilter ? advanceFilters?.[type] || [] : useAppSelector(selectUserFiltedByType(type));
  const tableColumns = useAppSelector(selectUserColumns);
  const isMultiSort = useAppSelector(selectDataTableIsMultiSort);
  const filterRef = useRef<any>(null);
  const isAccordionExpanded = expandedFilters.includes(type) || false;

  useEffect(() => {
    (filterRef.current as RefObject<any>['current'])?.querySelector('input')?.focus();
  }, []);

  const applyAssigneeFilter = (_: SyntheticEvent, selection: UserProfile) => {
    if (!selectedUsers.some((user: UserProfile) => user.email === selection.email)) {
      const newValue = {
        type,
        selections: [...selectedUsers, selection].map((user) => ({
          id: user.id,
          username: user.username,
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name
        }))
      };
      if (isAdvanceFilter && onAdvanceFiltersChange) {
        onAdvanceFiltersChange(newValue);
      } else {
        dispatch(applyFilter(newValue));
      }
      const hasPriorityCol = tableColumns.find((col) => col.id === 'priority');

      if (!isMultiSort && hasPriorityCol) {
        dispatch(setSorting({ id: 'priority', order: 'asc' }));
      }
    }
  };

  const removeUser = (id: number) => {
    const selections = selectedUsers.filter((user: UserProfile) => user.id !== id);
    const newValue = { type, selections };
    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter(newValue));
    }
  };

  const handleClear = () => {
    const newValue = { type, selections: null };
    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter(newValue));
    }
  };

  return isFiltersV2 && !isQuickFilters ? (
    <FilterAccordion
      label={type === 'managed_by_filter' ? 'Managed By' : 'Assignee'}
      expanded={isAccordionExpanded}
      count={selectedUsers?.length}
      showClear={selectedUsers?.length > 0}
      focusElementOnExpand={filterRef}
      onClear={handleClear}
      onChange={() => onExpand?.(type)}
    >
      <UserSearch
        onSelect={applyAssigneeFilter}
        isTableFilter
        type={type}
        innerRef={filterRef}
        isAdvanceFilter={isAdvanceFilter}
        advanceFilters={advanceFilters}
        onAdvanceFiltersChange={onAdvanceFiltersChange}
      />
    </FilterAccordion>
  ) : (
    <Box sx={{ m: 1 }}>
      <UserSearch onSelect={applyAssigneeFilter} isTableFilter type={type} isQuickFilters={isQuickFilters} innerRef={filterRef} />
      {selectedUsers.length > 0 && (
        <Grid container flexDirection="column" alignItems="flex-start" sx={{ mt: 3 }}>
          {selectedUsers.map((user: UserProfile) => (
            <Chip
              key={user.id}
              label={<UserDisplay user={user} />}
              onDelete={() => removeUser(user.id)}
              sx={{ mb: 1 }}
              deleteIcon={<PolarisIcon icon="close" />}
            />
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default FilterUser;
